import { trace } from '@opentelemetry/api';
import { isAxiosError } from 'axios';
import { env } from 'next-runtime-env';
import type { Logger } from 'pino';
import { v4 as uuidv4 } from 'uuid';

const isInfoLevelDisabled = (environment: string) =>
  ['pro'].includes(environment);

const generate32CharUUID = (): string => uuidv4().replace(/-/g, '');

function pick(object: any, keys: string[]): Record<string, unknown> {
  return keys.reduce((obj, key) => {
    if (object && Object.hasOwn(object, key)) {
      obj[key] = object[key];
    }
    return obj;
  }, {} as any);
}

export const getTraceInfo = () => {
  const currentSpan = trace.getActiveSpan();
  let trace_id = currentSpan?.spanContext()?.traceId;
  if (!trace_id) {
    trace_id = generate32CharUUID();
    console.info(
      'No trace_id found in current span, using generated: ',
      trace_id
    );
  }
  return {
    trace_id: 'mock-trace-id',
    span_id: 'mock-span-id',
  };
};

// sanitize input
export function sanitizeInput(input: any, depth = 5): any {
  if (depth == 0) return undefined;
  if (input === null || input === undefined) return input;
  if (typeof input === 'string') {
    return input?.replace(/[\r\n\x7F-\x9F<>'"()]/g, '');
  } else if (isAxiosError(input)) {
    const body = pick(input.toJSON(), ['message', 'stack', 'name']);
    return sanitizeInput(body, depth - 1);
  } else if (typeof input === 'object') {
    const sanitized = { ...input };
    for (const key in sanitized) {
      if (Object.getOwnPropertyDescriptor(sanitized, key)?.writable === false) {
        continue;
      }
      sanitized[key] = sanitizeInput(input[key], depth - 1);
    }
    return sanitized;
  }
  return input;
}
interface LogContext {
  tenantId?: string;
}

export function getLogger(name: string, context: LogContext = {}): Logger {
  // Conditionally import pino only on server-side
  let pino: any;
  if (typeof window === 'undefined') {
    try {
      pino = require('pino');
    } catch (e) {
      // Fallback if pino fails to load
      pino = null;
    }
  }

  // If running in browser or pino is not available, use console-based logger
  if (typeof window !== 'undefined' || !pino) {
    return console as unknown as Logger;
  }

  const logger = pino({
    name,
    serializers: {
      msg: sanitizeInput,
      err: sanitizeInput,
    },
  });

  const loggerError = logger.error.bind(logger);
  const loggerInfo = logger.info.bind(logger);
  logger.error = (obj: unknown, ...args: unknown[]) => {
    // Transform the object to {err: obj, traceId, span_id}
    loggerError({ err: obj, ...getTraceInfo(), ...context }, ...args);
  };

  logger.info = (obj: unknown, ...args: unknown[]) => {
    // Transform the object to {msg: obj, trace_id, span_id}
    loggerInfo({ msg: obj, ...getTraceInfo(), ...context }, ...args);
  };

  if (isInfoLevelDisabled(env('NEXT_PUBLIC_APP_ENV'))) {
    logger.info = (): void => undefined;
  }
  return logger;
}
